import { useCallback, useEffect, useMemo, useState } from 'react';
import { EditIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Form } from '@/pages/dashboard/tenant/settings/form';
import { settingsApi } from '@/lib/api';
import { BASE_URL } from '@/config/constants';
import type { Setting, SettingUpdateInput } from '@/types/entities';

interface SettingsListProps {
  tenantSlug: string;
}

const getImageAssetUrl = (tenantSlug: string, assetId: number) => {
  return `${BASE_URL}/assets/${tenantSlug}/${assetId}/content`;
};

export function SettingsList({ tenantSlug }: SettingsListProps) {
  const [settings, setSettings] = useState<Setting[]>([]);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSetting, setEditingSetting] = useState<Setting | undefined>();
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await settingsApi.getAllForTenantBySlug(tenantSlug);
      setSettings(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  }, [tenantSlug]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const filteredSettings = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return settings;

    return settings.filter((setting) => {
      return (
        setting.attribute.toLowerCase().includes(keyword) ||
        (setting.display_name || '').toLowerCase().includes(keyword) ||
        setting.value.toLowerCase().includes(keyword) ||
        (setting.type || 'text').toLowerCase().includes(keyword)
      );
    });
  }, [settings, search]);

  const handleEdit = (setting: Setting) => {
    setEditingSetting(setting);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (data: SettingUpdateInput) => {
    if (!editingSetting) return;

    await settingsApi.updateBySlug(tenantSlug, editingSetting.id, data);
    fetchSettings();
    setIsDialogOpen(false);
    setEditingSetting(undefined);
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-destructive/10 text-destructive p-3 rounded-md text-sm">
          {error}
        </div>
      )}

      <Input
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Search settings..."
      />

      {isLoading ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
          Loading settings...
        </div>
      ) : filteredSettings.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
          No settings found.
        </div>
      ) : (
        <div className="space-y-2">
          {filteredSettings.map((setting, index) => {
            const imageAssetId = setting.type === 'image' && setting.value ? Number(setting.value) : null;
            const isImage = setting.type === 'image' && imageAssetId !== null && !Number.isNaN(imageAssetId);

            return (
              <div key={setting.id} className="rounded-lg border bg-card p-3">
                <div className="flex flex-col gap-3 md:flex-row md:items-center">
                  <div className="w-full font-mono text-xs text-muted-foreground md:w-14">{index + 1}</div>

                  <div className="w-full md:w-56">
                    <Label className="text-sm">{setting.display_name || setting.attribute}</Label>
                  </div>

                  <div className="w-full md:flex-1">
                    {isImage ? (
                      <div className="h-10 w-10 overflow-hidden rounded-md border bg-muted/20">
                        <img
                          src={getImageAssetUrl(tenantSlug, imageAssetId)}
                          alt={setting.attribute}
                          className="h-full w-full object-cover"
                        />
                      </div>
                    ) : (
                      <Input
                        readOnly
                        value={setting.value}
                        className="w-full"
                        aria-label={`Value for ${setting.attribute}`}
                      />
                    )}
                  </div>

                  <div className="text-xs text-muted-foreground md:w-24">{setting.type || 'text'}</div>

                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(setting)} className="h-8 w-8">
                      <EditIcon className="size-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Form
        open={isDialogOpen}
        onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            setEditingSetting(undefined);
          }
        }}
        onSubmit={handleSubmit}
        onCancel={() => {
          setIsDialogOpen(false);
          setEditingSetting(undefined);
        }}
        setting={editingSetting}
        tenantSlug={tenantSlug}
        canEditType={false}
      />
    </div>
  );
}
